import { SP } from "./namespace";
import { SPELLS, SPELL_ORDER, clamp, escapeHtml, spellTipHTML } from "../client";
SP.scenes = Object.fromEntries(
  SPELL_ORDER.filter(id => SPELLS[id]).map(id => [id, SP.makeScene(id)])
);
let _previewMoveRaf = null;
let _previewMovePos = null;

function schedulePreviewMove(fn, x, y) {
  _previewMovePos = { fn, x, y };
  if (_previewMoveRaf) return;
  _previewMoveRaf = requestAnimationFrame(() => {
    _previewMoveRaf = null;
    const p = _previewMovePos;
    if (!p) return;
    p.fn(p.x, p.y);
  });
}

SP.Tip = {
  el: null,
  raf: null,
  spellId: null,
  sim: null,
  loopFill: null,
  lastT: 0,

  ensure() {
    if (!this.el) {
      this.el = document.createElement("div");
      this.el.className = "tooltip spell-preview-tip hidden";
      this.el.setAttribute("role", "tooltip");
      document.body.appendChild(this.el);
    }
    return this.el;
  },

  show(spellId, x, y, slotOverride) {
    if (SP.useSimPreview && SP.useSimPreview(spellId)) {
      this.stopLoop();
      this.spellId = null;
      this.sim = null;
      if (this.el) this.el.classList.add("hidden");
      if (SP.SimTip.show(spellId, x, y, slotOverride)) return true;
    }
    SP.SimTip.hide();
    const scene = SP.scenes[spellId];
    if (!scene) { this.hide(); return false; }
    this.spellId = spellId;
    const el = this.ensure();
    const cap = SP.captionFor(spellId);
    const capHtml = cap
      ? `<p class="spell-preview-caption">${escapeHtml(cap)}</p>`
      : "";
    el.innerHTML =
      `<div class="spell-preview-tip-text">${spellTipHTML(spellId, slotOverride)}</div>` +
      capHtml +
      `<div class="spell-preview-stage">` +
      `<canvas class="spell-preview-canvas" width="${scene.w}" height="${scene.h}" aria-hidden="true"></canvas>` +
      `<div class="spell-preview-loop" aria-hidden="true"><div class="spell-preview-loop-fill"></div></div>` +
      `</div>`;
    el.classList.remove("hidden");
    this.loopFill = el.querySelector(".spell-preview-loop-fill");
    const canvas = el.querySelector(".spell-preview-canvas");
    const ctx = canvas.getContext("2d");
    this.sim = {
      scene, ctx,
      particles: [],
      shockwaves: [],
      hitSpawned: false,
    };
    this.lastT = performance.now() / 1000;
    this.stopLoop();
    const tick = () => {
      if (!this.sim || this.spellId !== spellId) return;
      const now = performance.now() / 1000;
      const dt = clamp(now - this.lastT, 0.001, 0.05);
      this.lastT = now;
      scene.draw(this.sim, now, dt);
      const loop = scene.loop || 2.9;
      if (this.loopFill) this.loopFill.style.width = ((now % loop) / loop * 100) + "%";
      this.raf = requestAnimationFrame(tick);
    };
    tick();
    this.move(x, y);
    return true;
  },

  move(x, y, immediate) {
    if (SP.SimTip.spellId || SP.SimTip.passiveId) {
      if (immediate) SP.SimTip.move(x, y, true);
      else SP.SimTip.move(x, y);
      return;
    }
    const el = this.el;
    if (!el || el.classList.contains("hidden")) return;
    const r = el.getBoundingClientRect();
    let nx = x + 18, ny = y + 14;
    if (nx + r.width > window.innerWidth - 10) nx = x - r.width - 18;
    if (ny + r.height > window.innerHeight - 10) ny = y - r.height - 14;
    if (nx < 8) nx = 8;
    if (ny < 8) ny = 8;
    el.style.left = nx + "px";
    el.style.top = ny + "px";
  },

  stopLoop() {
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
  },

  hide() {
    if (_previewMoveRaf) {
      cancelAnimationFrame(_previewMoveRaf);
      _previewMoveRaf = null;
    }
    _previewMovePos = null;
    this.stopLoop();
    SP.SimTip.hide();
    this.spellId = null;
    this.sim = null;
    this.loopFill = null;
    if (this.el) this.el.classList.add("hidden");
  },
};

SP.isOverPreviewPopover = function(x, y) {
  for (const el of [SP.Tip.el, SP.SimTip.el]) {
    if (!el || el.classList.contains("hidden")) continue;
    const r = el.getBoundingClientRect();
    if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return true;
  }
  return false;
};

SP.wireCatalog = function(catalog) {
  if (!catalog || catalog.dataset.spellPreviewWired) return;
  catalog.dataset.spellPreviewWired = "1";
  SP.ensureCatalogPreviewWire();
};

SP.ensureCatalogPreviewWire = function() {
  if (SP._catalogPreviewWired) return;
  SP._catalogPreviewWired = true;
  let current = null;

  document.addEventListener("mousemove", (e) => {
    if (window.shopPopoversBlocked?.(e.clientX, e.clientY)) {
      if (current) { current = null; SP.Tip.hide(); SP.SimTip.hide(); }
      return;
    }
    if (!SP.isEnabled()) {
      if (current) { current = null; SP.Tip.hide(); }
      return;
    }
    const target = e.target as Element | null;
    const card = target?.closest?.("[data-spell-preview], [data-synergy-preview]");
    if (card?.closest("#draft")) return;
    const inHost = card?.closest?.("[data-spell-preview-wired]");
    const overPopover = SP.isOverPreviewPopover(e.clientX, e.clientY);

    if (!inHost && !overPopover) {
      if (current) { current = null; SP.Tip.hide(); }
      return;
    }
    if (!card) {
      if (overPopover) return;
      if (current) { current = null; SP.Tip.hide(); }
      return;
    }
    if (!card.closest("[data-spell-preview-wired]")) {
      if (current) { current = null; SP.Tip.hide(); }
      return;
    }

    const synergyId = card.getAttribute("data-synergy-preview");
    if (synergyId) {
      if (card.hasAttribute("data-synergy-partner-blocked")) {
        if (current) { current = null; SP.Tip.hide(); SP.SimTip.hide(); }
        return;
      }
      const spellId = card.getAttribute("data-tip-spell") || "";
      if (card !== current) {
        current = card;
        SP.Tip.hide();
        if (!SP.SimTip.showSynergy(spellId, synergyId, e.clientX, e.clientY, card)) {
          current = null;
        }
      } else SP.SimTip.move(e.clientX, e.clientY);
      return;
    }
    const id = card.getAttribute("data-spell-preview");
    if (!SP.has(id)) return;
    if (card !== current) {
      current = card;
      SP.Tip.hide();
      SP.Tip.show(id, e.clientX, e.clientY);
    } else schedulePreviewMove((x, y) => SP.Tip.move(x, y), e.clientX, e.clientY);
  });
};

SP.wire = function() {
  SP.wireCatalog(document.getElementById("spellbookCatalog"));
  SP.wireCatalog(document.getElementById("shopGrid"));
  SP.wireCatalog(document.getElementById("patchNotesBody"));
};
